import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Seo, { SITE_NAME, SITE_URL } from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SceneCard from '@/components/feature/SceneCard';
import { categories } from '@/mocks/categories';
import { scenes } from '@/mocks/scenes';

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const queryParam = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(queryParam);

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  const filtered = useMemo(() => {
    const trimmed = queryParam.trim().toLowerCase();
    return scenes.filter((scene) => {
      const matchCategory = activeCategory === 'all' || scene.category === activeCategory;
      if (!matchCategory) return false;
      if (!trimmed) return true;
      return (
        scene.title.toLowerCase().includes(trimmed) ||
        scene.excerpt.toLowerCase().includes(trimmed) ||
        scene.tags.some((tag) => tag.toLowerCase().includes(trimmed)) ||
        scene.categoryName.toLowerCase().includes(trimmed)
      );
    });
  }, [activeCategory, queryParam]);

  const applyCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === 'all') {
      next.delete('category');
    } else {
      next.set('category', slug);
    }
    setSearchParams(next);
  };

  const applySearch = () => {
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const activeName =
    activeCategory === 'all'
      ? '全部内容'
      : categories.find((item) => item.slug === activeCategory)?.name ?? '全部内容';

  const topicsJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${SITE_NAME} - 话题分类`,
      url: `${SITE_URL}/topics`,
      inLanguage: 'zh-CN',
      description: '按热评、弹幕、名场面、群聊高能、接梗合集与梗图速递分类浏览玩梗现场。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: '话题分类', item: `${SITE_URL}/topics` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${activeName} 名场面列表`,
      itemListElement: filtered.map((scene, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: scene.title,
        url: `${SITE_URL}/scene/${scene.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`话题分类 | ${SITE_NAME} - 热评弹幕名场面分类浏览`}
        description="按热评现场、弹幕玩梗、名场面、群聊高能、接梗合集与梗图速递六大分类浏览玩梗现场，支持关键词搜索，快速找到你最想看的那一段。"
        keywords="糖心吃瓜分类,玩梗分类,热评,弹幕,名场面,群聊高能"
        path="/topics"
        jsonLd={topicsJsonLd}
      />

      <Navbar />

      <main className="pb-16 pt-24 md:pt-28">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Breadcrumb items={[{ label: '话题分类' }]} />

          <header className="mt-5">
            <h1 className="text-3xl text-foreground-950 md:text-5xl">话题分类</h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              六大分类覆盖了站内最常出现的玩梗场景。你可以直接用分类切换，也可以用关键词搜索，
              找到想看的热评、弹幕或群聊片段。
            </p>
          </header>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyCategory('all')}
                  aria-pressed={activeCategory === 'all'}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    activeCategory === 'all'
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  全部内容
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => applyCategory(cat.slug)}
                    aria-pressed={activeCategory === cat.slug}
                    className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                      activeCategory === cat.slug
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <form
                className="flex w-full gap-2 lg:w-auto"
                onSubmit={(event) => {
                  event.preventDefault();
                  applySearch();
                }}
              >
                <div className="relative flex-1 lg:w-64">
                  <i
                    className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索名场面、热评或梗词"
                    aria-label="搜索玩梗现场"
                    className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </form>
            </div>

            <p className="mt-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
              当前：{activeName}
              {queryParam && ` · 关键词「${queryParam}」`}
              {' · '}共 {filtered.length} 条整理内容
            </p>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((scene, index) => (
                <SceneCard key={scene.id} scene={scene} rank={index + 1} />
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" aria-hidden="true" />
              </span>
              <h2 className="mt-4 text-lg text-foreground-950">暂时没找到匹配的现场</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者直接切换到「全部内容」看看今天最热的那几段。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams(new URLSearchParams());
                }}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}

          <section className="mt-14 rounded-lg bg-secondary-950 p-6 text-background-100 md:p-8">
            <h2 className="text-xl text-background-50 md:text-2xl">按分类浏览的小提示</h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <div>
                <h3 className="text-base text-accent-300">先看热度再挑内容</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  热度高的条目通常是最近讨论最集中的现场，适合想快速跟上节奏的时候先看。
                </p>
              </div>
              <div>
                <h3 className="text-base text-accent-300">用关键词精准定位</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  直接在搜索框输入梗词或台词片段，就能跳过分类筛选，一步跳到对应内容。
                </p>
              </div>
              <div>
                <h3 className="text-base text-accent-300">分类会持续补充</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  每个分类都会定期加入新整理的名场面与热评，旧条目则归入对应分类长期保存。
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}